import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  type MarkPiece,
  type QuestionPiece,
  type ReplyPiece,
  caseNumber,
  questionPieces,
  replyPieces,
} from '@/lib/review-transcript';
import { cn } from '@/lib/utils';
import type { ReviewExtract, ReviewQuestionNote } from '@matter_extractor/shared';
import { Fragment, type KeyboardEvent, type MouseEvent } from 'react';

/**
 * The words of a message with their marks (relecture.md §4.3, D25, D29).
 *
 * ## What is drawn, and what the page never draws
 *
 * A passage ranged is a BLOCK tinted bleu nuit, set apart from its neighbours by
 * a margin, its cases in a full pastille at its head - « 1, 6 ». Three states
 * read without opening anything: ranged (the block), annotated (a folded corner
 * at the top right), questioned (an amber block, ringed, with a « ? » on the
 * pastille). No underline anywhere. A question the interviewer asked has its own
 * family - a red or a green ground, « Va » or « Ne va pas » at its head.
 *
 * ## The pastille is not text
 *
 * It is `::before` content read off `data-k`, and every accessible name is an
 * attribute. `textContent` of the element that holds a message is therefore the
 * message and nothing else, which is what makes the length of the text before a
 * selection its position (`review-selection.ts`). A span of text added inside a
 * mark - an `sr-only` « rangé en 1 » - would shift every position after it.
 */

const MARK =
  'mx-[3px] cursor-pointer rounded-sm px-1 py-px [box-decoration-break:clone] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const PASTILLE =
  'before:mr-1 before:inline-block before:rounded-[3px] before:px-1 before:align-[1px] before:font-bold before:text-[10px] before:leading-[14px] before:text-white before:content-[attr(data-k)]';
const RANGED = 'bg-bleu-nuit-tint before:bg-bleu-nuit';
const QUESTIONED =
  'bg-jaune-tint shadow-[inset_0_0_0_1px_var(--color-jaune-deep)] before:bg-jaune-deep';
const FOLD_RANGED =
  '[background-image:linear-gradient(225deg,var(--color-bleu-nuit)_0_6px,transparent_6px)]';
const FOLD_QUESTIONED =
  '[background-image:linear-gradient(225deg,var(--color-jaune-deep)_0_6px,transparent_6px)]';

/** The class of a mark: ranged or questioned, folded when annotated, with its pastille. */
export function markClass(piece: Pick<MarkPiece, 'doubt' | 'noted' | 'label'>): string {
  return cn(
    MARK,
    piece.doubt ? QUESTIONED : RANGED,
    piece.noted && (piece.doubt ? FOLD_QUESTIONED : FOLD_RANGED),
    piece.label !== null && PASTILLE,
  );
}

/** What a mark says to a screen reader: the words, then where they are ranged. */
function markName(
  piece: MarkPiece,
  byId: ReadonlyMap<string, ReviewExtract>,
  order: readonly string[],
) {
  const numbers = [
    ...new Set(piece.extractIds.map((id) => caseNumber(order, byId.get(id)?.objectiveId ?? ''))),
  ]
    .filter((number) => number > 0)
    .sort((a, b) => a - b)
    .join(', ');
  const state = [piece.doubt ? 'avec doute' : null, piece.noted ? 'annoté' : null]
    .filter((part) => part !== null)
    .join(', ');
  return `${piece.text} (rangé en ${numbers}${state === '' ? '' : `, ${state}`})`;
}

export interface MarkedReplyProps {
  readonly turn: number;
  readonly reply: string;
  readonly extracts: readonly ReviewExtract[];
  readonly order: readonly string[];
  /** A mark was pressed: the extracts under it, and the element to anchor the popover on. */
  readonly onOpen: (ids: readonly string[], anchor: HTMLElement) => void;
}

/** A reply, with the passages ranged in it marked. */
export function MarkedReply({ turn, reply, extracts, order, onOpen }: MarkedReplyProps) {
  const pieces: ReplyPiece[] = replyPieces(reply, extracts, order);
  const byId = new Map(extracts.map((extract) => [extract.id, extract]));

  const open = (piece: MarkPiece, anchor: HTMLElement): void => onOpen(piece.extractIds, anchor);

  return (
    <div
      data-testid={REVIEW_TEST_IDS.reply}
      data-review-text="reply"
      data-turn={turn}
      className="min-w-0 cursor-text whitespace-pre-wrap text-sm leading-[26px]"
    >
      {pieces.map((piece) =>
        piece.kind === 'plain' ? (
          <Fragment key={`${piece.start}-plain`}>{piece.text}</Fragment>
        ) : (
          <span
            key={`${piece.start}-mark`}
            // A button is a box and cannot wrap across lines the way a passage of text does.
            // biome-ignore lint/a11y/useSemanticElements: the mark is inline text that stays selectable
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-label={markName(piece, byId, order)}
            data-testid={REVIEW_TEST_IDS.mark}
            data-ids={piece.extractIds.join(',')}
            data-label={piece.label ?? undefined}
            data-k={piece.label ?? undefined}
            data-doubt={piece.doubt}
            data-noted={piece.noted}
            className={markClass(piece)}
            onClick={(event: MouseEvent<HTMLElement>) => {
              // A drag across the mark ends in a click: the selection is the gesture then.
              if (!(window.getSelection()?.isCollapsed ?? true)) return;
              open(piece, event.currentTarget);
            }}
            onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              open(piece, event.currentTarget);
            }}
          >
            {piece.text}
          </span>
        ),
      )}
    </div>
  );
}

const NOTED_BAD =
  "bg-rouge-tint before:mr-1 before:font-bold before:text-micro before:text-rouge-deep before:uppercase before:tracking-[0.04em] before:content-['Ne_va_pas']";
const NOTED_GOOD =
  "bg-vert-tint before:mr-1 before:font-bold before:text-micro before:text-vert-deep before:uppercase before:tracking-[0.04em] before:content-['Va']";

export interface AnnotatedQuestionProps {
  readonly turn: number;
  readonly message: string;
  readonly notes: readonly ReviewQuestionNote[];
}

/** An interviewer's message, with the passages annotated « Va » or « Ne va pas ». */
export function AnnotatedQuestion({ turn, message, notes }: AnnotatedQuestionProps) {
  const pieces: QuestionPiece[] = questionPieces(message, notes);
  return (
    <div
      data-testid={REVIEW_TEST_IDS.question}
      data-review-text="question"
      data-turn={turn}
      className="min-w-0 cursor-text whitespace-pre-wrap text-sm leading-[26px]"
    >
      {pieces.map((piece) =>
        piece.kind === 'plain' ? (
          <Fragment key={`${piece.start}-plain`}>{piece.text}</Fragment>
        ) : (
          <span
            key={`${piece.start}-noted`}
            data-testid={REVIEW_TEST_IDS.notedPassage}
            data-verdict={piece.verdict}
            className={cn(
              'rounded-sm px-1 [box-decoration-break:clone]',
              piece.verdict === 'bad' ? NOTED_BAD : NOTED_GOOD,
            )}
          >
            {piece.text}
          </span>
        ),
      )}
    </div>
  );
}
